import type { EventTuple } from './types'
import type { VoxelMap } from '../voxelTypes'
import { encodeDevalue, decodeDevalue } from '@/persistence/devalueCodec'
import type { SnapshotMeta } from '@/document/model'
import { hydrateMeta } from '@/document/hydration'
import { isChunkedVoxelGrid } from '@/storage/grid/ChunkedVoxelGrid'
import { withDocumentBounds } from '@/storage/grid/documentBase'

export type DurableUiSnapshot = any

// Dev assertion (docs/large-volume-plan.md §3.6): durable event payloads must never carry a document
// base (ChunkedVoxelGrid). Bases live only in checkpoints, which serialize through the devalue grid
// reviver. The check is shallow (payload arguments only) to stay cheap on long strokes.
const ASSERT_PAYLOADS = !!import.meta.env?.DEV
function assertNoGridInPayload(event: EventTuple): void {
  if (!ASSERT_PAYLOADS) return
  for (let i = 1; i < event.length; i++) {
    if (isChunkedVoxelGrid(event[i])) {
      throw new Error(`AppEventLog: event '${String(event[0])}' carries a ChunkedVoxelGrid in its payload`)
    }
  }
}

// Logical durable version per entry (monotonic within this log)
type AppEntryBase = { commitId: number }
type AppEventEntry = AppEntryBase & { kind: 'event'; event: EventTuple }
type AppTransactionEntry = AppEntryBase & { kind: 'transaction'; events: EventTuple[]; description?: string }
type AppEntry = AppEventEntry | AppTransactionEntry

type AppCheckpoint = {
  index: number
  commitId: number
  base: VoxelMap
  ui: DurableUiSnapshot
  meta: SnapshotMeta
  /** Chunk bytes first allocated by this base's version (`freshChunkBytes`), 0 for non-grid bases. */
  bytes: number
}

// --- Checkpoint policy flags (docs/large-volume-plan.md §3.3, §4 row S4) -------------------------

export const KEEP_REDO_CHECKPOINTS_STORAGE_KEY = 'huebo:keep-redo-checkpoints'
export const CHECKPOINT_BUDGET_STORAGE_KEY = 'huebo:checkpoint-budget-mb'
const MB = 1024 * 1024
/** 64 MB on low-memory or touch devices, 192 MB on desktop (plan §3.3). */
export const CHECKPOINT_BYTE_BUDGET_LOW = 64 * MB
export const CHECKPOINT_BYTE_BUDGET_HIGH = 192 * MB
/** Count cap, kept for non-grid checkpoints whose byte cost is unknown (0). */
export const MAX_CHECKPOINTS = 500

function readLocal(key: string): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(key)
  } catch {
    return null
  }
}

export function resolveCheckpointByteBudget(
  overrideMb: string | null,
  deviceMemoryGb: number | undefined,
  isTouch: boolean,
): number {
  const mb = overrideMb === null ? NaN : Number(overrideMb)
  if (Number.isFinite(mb) && mb > 0) return Math.floor(mb * MB)
  if (deviceMemoryGb !== undefined) return deviceMemoryGb <= 4 ? CHECKPOINT_BYTE_BUDGET_LOW : CHECKPOINT_BYTE_BUDGET_HIGH
  return isTouch ? CHECKPOINT_BYTE_BUDGET_LOW : CHECKPOINT_BYTE_BUDGET_HIGH
}

function readCheckpointByteBudgetOnce(): number {
  const nav = typeof navigator === 'undefined' ? undefined : (navigator as Navigator & { deviceMemory?: number })
  const deviceMemory = typeof nav?.deviceMemory === 'number' ? nav.deviceMemory : undefined
  const isTouch = !!nav && ((nav.maxTouchPoints ?? 0) > 0)
  return resolveCheckpointByteBudget(readLocal(CHECKPOINT_BUDGET_STORAGE_KEY), deviceMemory, isTouch)
}

let checkpointByteBudget = readCheckpointByteBudgetOnce()
let keepRedoCheckpoints = readLocal(KEEP_REDO_CHECKPOINTS_STORAGE_KEY) !== '0'

/** Σ `AppCheckpoint.bytes` above which checkpoints are thinned. */
export function getCheckpointByteBudget(): number {
  return checkpointByteBudget
}

/** Overrides the session budget without touching localStorage (tests and benches). */
export function setCheckpointByteBudget(bytes: number): void {
  checkpointByteBudget = bytes
}

/** Whether undo keeps checkpoints past the pointer so redo restores instead of replaying. */
export function keepRedoCheckpointsEnabled(): boolean {
  return keepRedoCheckpoints
}

/** Overrides the session flag without touching localStorage (tests). */
export function setKeepRedoCheckpointsEnabled(enabled: boolean): void {
  keepRedoCheckpoints = enabled
}

function checkpointBytesOf(base: VoxelMap): number {
  return isChunkedVoxelGrid(base) ? base.freshChunkBytes : 0
}

type DraftState = {
  startIndex: number
  baseCheckpoint: { base: VoxelMap; ui: any; meta: SnapshotMeta } | null
}

export class AppEventLog {
  private entries: AppEntry[] = []
  private pointer = -1
  private checkpoints: AppCheckpoint[] = []
  // Monotonic durable version; increments on durable append, not on undo/redo
  private commitIdCounter = 0
  // Commit id associated with the durable baseline (checkpoint at index -1)
  private durableBaseCommitId = 0
  // Active draft session (null when not drafting)
  private draftState: DraftState | null = null
  constructor() {}

  canUndo(): boolean { return this.pointer > -1 }
  canRedo(): boolean { return this.pointer < this.entries.length - 1 }
  getPointer(): number { return this.pointer }
  private getCheckpointCommitIdAtOrBefore(index: number): number | null {
    for (let i = this.checkpoints.length - 1; i >= 0; i--) {
      const cp = this.checkpoints[i]
      if (cp.index <= index) return cp.commitId
    }
    return null
  }

  private getCommitIdAt(index: number): number {
    if (index >= 0) {
      return (this.entries[index] as AppEntryBase | undefined)?.commitId || this.durableBaseCommitId
    }
    return this.getCheckpointCommitIdAtOrBefore(index) ?? this.durableBaseCommitId
  }

  /** Current durable version at pointer (0 if empty) */
  getHeadCommitId(): number {
    return this.getCommitIdAt(this.pointer)
  }

  /**
   * Get the durable commitId - ignores ephemeral draft state.
   * Returns the commitId before draft started if draft is active, otherwise returns head commitId.
   * This represents the last committed state that should be persisted.
   */
  getDurableCommitId(): number {
    if (this.draftState) {
      const idx = this.draftState.startIndex
      return this.getCommitIdAt(idx)
    }
    return this.getHeadCommitId()
  }

  /**
   * Seed the durable baseline commit id. Intended for log hydration/loading.
   * Ensures future commits continue monotonically from this value.
   */
  setDurableBaseCommitId(commitId: number): void {
    const cid = Number.isFinite(commitId) && commitId > 0 ? Math.floor(commitId) : 0
    this.durableBaseCommitId = cid
    if (this.commitIdCounter < cid) this.commitIdCounter = cid
  }

  appendEvent(event: EventTuple) {
    assertNoGridInPayload(event)
    this.truncateRedo()
    const entry: AppEventEntry = { kind: 'event', event, commitId: ++this.commitIdCounter }
    this.entries.push(entry)
    this.pointer = this.entries.length - 1
  }

  appendTransaction(events: EventTuple[], description?: string) {
    if (ASSERT_PAYLOADS) for (const ev of events) assertNoGridInPayload(ev)
    this.truncateRedo()
    const entry: AppTransactionEntry = { kind: 'transaction', events: events.slice(), description, commitId: ++this.commitIdCounter }
    this.entries.push(entry)
    this.pointer = this.entries.length - 1
  }

  // --- Consequences of a step ----------------------------------------------------
  /**
   * Whether `extendLastStep` would accept events now: no draft is open, the pointer is at the head
   * (nothing to redo) and the entry there is an undo boundary (a committed step, e.g. a stroke).
   */
  canExtendLastStep(): boolean {
    if (this.draftState) return false
    if (this.pointer < 0 || this.pointer !== this.entries.length - 1) return false
    return isUndoBoundaryEntry(this.entries[this.pointer])
  }

  /**
   * Folds follow-up events into the step that just committed, so an automatic reaction to a user's
   * step (water flooding a dug channel, a pile slumping after a drop) undoes and redoes with it as
   * one step. The entry at the pointer becomes a transaction of its events followed by `events`,
   * with a new commit id (the document changed, so persistence sees a new durable version).
   * Checkpoints at the pointer described the step without its consequences, so they are dropped;
   * the caller checkpoints the new state (the dispatch layer does, `extendLastStep` in
   * src/events/atoms.ts). Returns false and changes nothing when `canExtendLastStep()` is false.
   *
   * React only to the user's own steps (a stroke's commit), never to undo/redo: extending after
   * an undo would fold the reaction into the previous step and is refused anyway once a redo tail
   * exists.
   */
  extendLastStep(events: EventTuple[]): boolean {
    if (events.length === 0 || !this.canExtendLastStep()) return false
    if (ASSERT_PAYLOADS) for (const ev of events) assertNoGridInPayload(ev)
    const index = this.pointer
    const entry = this.entries[index]
    const prior = entry.kind === 'event' ? [entry.event] : entry.events
    this.entries[index] = {
      kind: 'transaction',
      events: [...prior, ...events],
      description: entry.kind === 'transaction' ? entry.description : undefined,
      commitId: ++this.commitIdCounter,
    }
    this.checkpoints = this.checkpoints.filter((cp) => cp.index !== index)
    return true
  }

  private truncateRedo() {
    if (this.pointer < this.entries.length - 1) {
      this.entries = this.entries.slice(0, this.pointer + 1)
      // Drop checkpoints beyond pointer
      this.checkpoints = this.checkpoints.filter(cp => cp.index <= this.pointer)
    }
  }

  // --- Draft range operations -------------------------------------------------
  /**
   * Remove entries in [start, endExclusive) and optionally insert a single replacement
   */
  private spliceEntries(start: number, endExclusive: number, replacement?: AppEntry) {
    const insertCount = replacement ? 1 : 0
    this.updateCheckpointsForSplice(start, endExclusive, start, insertCount, /*allowKeepFromDeleted*/ insertCount > 0)
    const deleteCount = endExclusive - start
    if (replacement) this.entries.splice(start, deleteCount, replacement)
    else this.entries.splice(start, deleteCount)
    this.adjustPointerForSplice(start, endExclusive, insertCount)
  }

  /** Truncate the log from indexInclusive to the end */
  truncateFrom(indexInclusive: number) {
    const start = Math.max(0, indexInclusive)
    const endExclusive = this.entries.length
    this.spliceEntries(start, endExclusive)
  }

  /** Replace [start, endExclusive) with a single event or transaction */
  replaceRangeWithEvents(start: number, endExclusive: number, events: EventTuple[], description?: string) {
    // Preserve the durable version of the last replaced entry (coalesce/rewrite without new commit)
    const cid = (this.entries[endExclusive - 1] as AppEntryBase | undefined)?.commitId || this.getHeadCommitId()
    let replacement: AppEntry
    if (events.length === 1) replacement = { kind: 'event', event: events[0], commitId: cid }
    else replacement = { kind: 'transaction', events: events.slice(), description, commitId: cid }
    this.spliceEntries(start, endExclusive, replacement)
  }

  /**
   * Flatten a range of entries to EventTuple[]
   */
  private flattenRange(start: number, end: number): EventTuple[] {
    const flat: EventTuple[] = []
    for (let i = start; i < end; i++) {
      const entry = this.entries[i]
      if (entry.kind === 'event') flat.push(entry.event)
      else flat.push(...entry.events)
    }
    return flat
  }

  /**
   * Coalesce a range of entries [start, endExclusive) using a caller-provided reducer.
   * - Flattens the range to EventTuple[] first.
   * - Attempts to fold with `coalesce(acc, next)`; if any step returns null, folding fails.
   * - On success, replaces the range with the single coalesced event (preserving commit id).
   * - On failure, replaces the range with a single transaction containing the flattened events
   *   (preserving commit id) and an optional description.
   *
   * This expresses intent: compaction without changing durable semantics (no new commit id).
   */
  coalesceRangeWith(
    start: number,
    endExclusive: number,
    coalesce: (a: EventTuple, b: EventTuple) => EventTuple | null
  ): void {
    const s = Math.max(0, Math.min(start, endExclusive))
    const e = Math.max(s, Math.min(endExclusive, this.entries.length))
    if (e <= s) return

    const flat = this.flattenRange(s, e)
    if (flat.length === 0) return

    let combined: EventTuple | null = null
    for (const ev of flat) {
      if (!combined) {
        combined = ev
        continue
      }
      const merged = coalesce(combined, ev)
      if (merged) {
        combined = merged
      } else {
        combined = null
        break
      }
    }

    if (combined) {
      this.replaceRangeWithEvents(s, e, [combined])
    } else {
      const desc = `tx(${e - s})`
      this.replaceRangeWithEvents(s, e, flat, desc)
    }
  }

  private updateCheckpointsForSplice(start: number, endExclusive: number, insertIndex: number, insertCount: number, allowKeepFromDeleted: boolean) {
    let keep: AppCheckpoint | null = null
    const survivors: AppCheckpoint[] = []
    for (const cp of this.checkpoints) {
      if (cp.index >= start && cp.index < endExclusive) {
        if (allowKeepFromDeleted) {
          if (!keep || cp.index >= keep.index) keep = cp
        }
        continue
      }
      survivors.push(cp)
    }
    const deleteCount = endExclusive - start
    const delta = insertCount - deleteCount
    if (delta !== 0) {
      for (const cp of survivors) {
        if (cp.index >= endExclusive) cp.index += delta
      }
    }
    if (keep) {
      keep.index = insertIndex
      survivors.push(keep)
    }
    survivors.sort((a, b) => a.index - b.index)
    this.checkpoints = survivors
  }

  private adjustPointerForSplice(start: number, endExclusive: number, insertCount: number) {
    const deleteCount = endExclusive - start
    const delta = insertCount - deleteCount

    // If pointer is in deleted range, move to last inserted entry (or before deleted range if nothing inserted)
    if (this.pointer >= start && this.pointer < endExclusive) {
      this.pointer = insertCount > 0 ? (start + insertCount - 1) : (start - 1)
    }
    // If pointer is after deleted range, shift by delta
    else if (this.pointer >= endExclusive) {
      this.pointer += delta
    }

    // Invariant: pointer in [-1, entries.length - 1] by construction
    // -1 is valid (empty log), no clamping needed
  }

  private makeCheckpoint(index: number, snapshot: { base: VoxelMap; ui: DurableUiSnapshot; meta: SnapshotMeta }): AppCheckpoint {
    const cid = index >= 0
      ? ((this.entries[index] as AppEntryBase | undefined)?.commitId || this.durableBaseCommitId)
      : this.durableBaseCommitId
    return {
      index,
      commitId: cid,
      base: snapshot.base,
      ui: snapshot.ui,
      meta: hydrateMeta(snapshot.meta, 'strict'),
      bytes: checkpointBytesOf(snapshot.base),
    }
  }

  /**
   * Insert or replace the checkpoint at `index`, keeping `checkpoints` sorted by index.
   */
  private upsertCheckpoint(index: number, snapshot: { base: VoxelMap; ui: DurableUiSnapshot; meta: SnapshotMeta }) {
    const cp = this.makeCheckpoint(index, snapshot)
    let i = this.checkpoints.length
    while (i > 0 && this.checkpoints[i - 1].index > index) i--
    if (i > 0 && this.checkpoints[i - 1].index === index) this.checkpoints[i - 1] = cp
    else this.checkpoints.splice(i, 0, cp)
  }

  /**
   * Create a checkpoint capturing base voxel map + durable UI snapshot for fast replay
   */
  checkpoint(snapshot: { base: VoxelMap; ui: DurableUiSnapshot; meta: SnapshotMeta }) {
    const cp = this.makeCheckpoint(this.pointer, snapshot)
    this.durableBaseCommitId = cp.commitId
    const draft = this.draftState
    if (draft && this.pointer > draft.startIndex) {
      // While drafting, keep a single "draft head" checkpoint for the draft range instead of
      // one per append: a long stroke must never evict the pre-draft checkpoints.
      const last = this.checkpoints[this.checkpoints.length - 1]
      if (last && last.index > draft.startIndex) this.checkpoints[this.checkpoints.length - 1] = cp
      else this.checkpoints.push(cp)
      return
    }
    this.checkpoints.push(cp)
    this.thinCheckpoints()
  }

  /** Σ bytes over all checkpoints (an upper bound on the chunk memory they retain). */
  checkpointBytes(): number {
    let total = 0
    for (const cp of this.checkpoints) total += cp.bytes
    return total
  }

  /**
   * Cap checkpoint memory. Rather than shifting the oldest away (which makes undo-to-beginning
   * impossible), drop every other checkpoint from the older half, always keeping the oldest, the
   * pinned draft base and the pointer checkpoint. Passes repeat while the count exceeds
   * MAX_CHECKPOINTS or Σ bytes exceeds the byte budget; when the older half has nothing left to
   * drop, a pass covers the whole list. Replay from an older checkpoint is the accepted cost.
   */
  private thinCheckpoints() {
    const budget = checkpointByteBudget
    let bytes = this.checkpointBytes()
    while (this.checkpoints.length > MAX_CHECKPOINTS || bytes > budget) {
      const before = this.checkpoints.length
      this.thinPass(this.checkpoints.length >> 1)
      if (this.checkpoints.length === before) this.thinPass(this.checkpoints.length)
      if (this.checkpoints.length === before) break
      bytes = this.checkpointBytes()
    }
  }

  /** Drops every other droppable checkpoint among the first `endExclusive` entries. */
  private thinPass(endExclusive: number) {
    const pinnedIndex = this.draftState?.startIndex
    const pointer = this.pointer
    const survivors: AppCheckpoint[] = []
    let droppable = 0
    for (let i = 0; i < this.checkpoints.length; i++) {
      const cp = this.checkpoints[i]
      const protect = i === 0 || i >= endExclusive || cp.index === pinnedIndex || cp.index === pointer
      if (!protect && (droppable++ & 1) === 0) continue
      survivors.push(cp)
    }
    this.checkpoints = survivors
  }

  /**
   * Returns the latest checkpoint at or before the current pointer
   */
  getLatestCheckpoint(): AppCheckpoint | null {
    // Prefer the most recently created checkpoint at or before the pointer
    for (let i = this.checkpoints.length - 1; i >= 0; i--) {
      const cp = this.checkpoints[i]
      if (cp.index <= this.pointer) return cp
    }
    return null
  }

  /**
   * Get entries to replay from (checkpointIndexExclusive, pointer]
   */
  getEntriesAfter(index: number): AppEntry[] {
    const start = index + 1
    if (start < 0) return this.entries.slice(0, this.pointer + 1)
    return this.entries.slice(start, this.pointer + 1)
  }

  undo(): boolean {
    if (!this.canUndo()) return false

    // Find previous undo-boundary entry (skipping UI-only like selection/tool changes)
    let i = this.pointer - 1
    // Fixed: check boundary at i=0 too (was: i > 0)
    while (i >= 0 && !isUndoBoundaryEntry(this.entries[i])) {
      i--
    }
    // If we went below 0, there's no boundary to undo to (shouldn't happen if canUndo returned true)
    if (i < -1) i = -1

    this.pointer = i
    // Checkpoints past the pointer stay (plan §3.3): getLatestCheckpoint ignores them, truncateRedo
    // drops them when a new entry is appended, and redo restores from them instead of replaying.
    // `huebo:keep-redo-checkpoints=0` restores the old behaviour of dropping them here.
    if (!keepRedoCheckpoints && this.checkpoints.length > 0) {
      this.checkpoints = this.checkpoints.filter((cp) => cp.index <= this.pointer)
    }

    return true
  }

  redo(): boolean {
    // Find next undo-boundary entry
    let i = this.pointer + 1
    while (i < this.entries.length && !isUndoBoundaryEntry(this.entries[i])) {
      i++
    }
    if (i >= this.entries.length) return false
    this.pointer = i
    return true
  }

  historySummary(): string[] {
    return this.entries.map((e, i) => {
      const marker = i === this.pointer ? '→ ' : '  '
      const cid = (e as AppEntryBase).commitId
      if (e.kind === 'event') return `${marker}[#${cid}] ${e.event[0]}`
      return `${marker}[#${cid}] tx(${e.events.length}) ${e.description || ''}`
    })
  }

  /**
   * Debug helper: print event log with boundary markers
   */
  printUndoDebug(): void {
    // const lines = this.entries.map((e, i) => {
    //   const isBoundary = isUndoBoundaryEntry(e)
    //   const isPointer = i === this.pointer
    //   const marker = isPointer ? '→' : ' '
    //   const boundary = isBoundary ? '●' : '○'
    //   const cid = (e as AppEntryBase).commitId

    //   if (e.kind === 'event') {
    //     return `${marker} ${boundary} [${i}] #${cid} ${e.event[0]}`
    //   } else {
    //     const eventNames = e.events.map(ev => ev[0]).join(', ')
    //     return `${marker} ${boundary} [${i}] #${cid} tx(${e.events.length}): ${eventNames}`
    //   }
    // })
    // console.log('Event log (● = boundary, → = pointer):\n' + lines.join('\n'))
  }

  // --- Serialization (boundaries) ------------------------------------------------
  // Serialize entire log state (entries + pointer + checkpoints) using devalue
  serialize(): string {
    const payload = {
      version: 3,
      pointer: this.pointer,
      commitIdCounter: this.commitIdCounter,
      entries: this.entries,
      checkpoints: this.checkpoints,
      durableBaseCommitId: this.durableBaseCommitId,
    }
    return encodeDevalue(payload)
  }

  // Deserialize into a new AppEventLog instance
  static deserialize(text: string): AppEventLog {
    const payload = decodeDevalue<any>(text)
    if (!payload || typeof payload !== 'object') throw new Error('Invalid log payload')
    const log = new AppEventLog()
    const entries = Array.isArray(payload.entries) ? payload.entries as AppEntry[] : []
    // Back-compat: if commitId missing, synthesize monotonically
    let cid = typeof payload.commitIdCounter === 'number' ? payload.commitIdCounter : 0
    const hasCommitIds = entries.every((e: any) => typeof e?.commitId === 'number')
    if (!hasCommitIds) {
      cid = 0
      for (let i = 0; i < entries.length; i++) (entries[i] as any).commitId = ++cid
    }
    log['entries'] = entries
    log['pointer'] = typeof payload.pointer === 'number' ? payload.pointer : 0
    const checkpoints: AppCheckpoint[] = Array.isArray(payload.checkpoints) ? payload.checkpoints : []
    for (const cp of checkpoints) {
      // Legacy (pre-S13) Immutable.Map checkpoints revive as whole-domain grids: clip to the document.
      if (isChunkedVoxelGrid(cp.base)) cp.base = withDocumentBounds(cp.base, cp.meta?.bounds)
      if (typeof cp.bytes !== 'number') cp.bytes = checkpointBytesOf(cp.base)
    }
    log['checkpoints'] = checkpoints
    log['commitIdCounter'] = typeof payload.commitIdCounter === 'number' ? payload.commitIdCounter : cid
    const baseCid = typeof payload.durableBaseCommitId === 'number'
      ? payload.durableBaseCommitId
      : (log['pointer'] >= 0
        ? ((log['entries'][log['pointer']] as AppEntryBase | undefined)?.commitId || 0)
        : 0)
    log['durableBaseCommitId'] = baseCid
    if (log['commitIdCounter'] < baseCid) log['commitIdCounter'] = baseCid
    return log
  }

  // --- Draft API ---------------------------------------------------------------
  /**
   * Begin a draft session. All subsequent events are tentative until commit/revert.
   * Drafts enable live preview with efficient undo and optional coalescing.
   * @param baseCheckpoint Optional checkpoint to restore on revert (avoids slow replay)
   */
  beginDraft(baseCheckpoint?: { base: VoxelMap; ui: any; meta: SnapshotMeta }): void {
    if (this.draftState) throw new Error('Draft already active')
    this.draftState = {
      startIndex: this.pointer,
      baseCheckpoint: baseCheckpoint || null
    }
    // Pin the pre-draft state at startIndex so undo after commit never has to replay from
    // (or fail to find) an older checkpoint.
    if (baseCheckpoint) this.upsertCheckpoint(this.pointer, baseCheckpoint)
  }

  /**
   * Commit the current draft, optionally coalescing events into a single transaction.
   * @param coalesce Optional reducer to combine draft events
   */
  commitDraft(coalesce?: (a: EventTuple, b: EventTuple) => EventTuple | null): void {
    if (!this.draftState) return
    const { startIndex, baseCheckpoint } = this.draftState
    const endExclusive = this.pointer + 1
    if (coalesce && endExclusive > startIndex + 1) {
      // Splicing keeps the draft-head checkpoint and moves it to the coalesced entry's index.
      this.coalesceRangeWith(startIndex + 1, endExclusive, coalesce)
      this.printUndoDebug()
    }
    this.draftState = null
    // Checkpoints at or before startIndex were untouched by the splice; make sure the
    // pre-stroke one is present so undoing the stroke is a direct checkpoint restore.
    if (baseCheckpoint && !this.checkpoints.some(cp => cp.index === startIndex)) {
      this.upsertCheckpoint(startIndex, baseCheckpoint)
    }
  }

  /**
   * Revert the current draft, discarding all events since beginDraft.
   * Restores the base checkpoint if provided.
   */
  revertDraft(): void {
    if (!this.draftState) return
    const { startIndex, baseCheckpoint } = this.draftState
    this.truncateFrom(startIndex + 1)
    this.draftState = null
    // Restore base checkpoint to avoid slow replay if the original was evicted
    if (baseCheckpoint) this.upsertCheckpoint(startIndex, baseCheckpoint)
  }

  /**
   * Check if a draft session is currently active.
   */
  isDraftActive(): boolean {
    return this.draftState !== null
  }

  /**
   * Get the base checkpoint from the current draft (if any).
   * Useful for rendering preview against the draft base instead of composed state.
   */
  getDraftBaseCheckpoint(): { base: VoxelMap; ui: any; meta: SnapshotMeta } | null {
    return this.draftState?.baseCheckpoint || null
  }
}

// An entry constitutes an undo/redo boundary if it contains any document-affecting event
function isUndoBoundaryEntry(entry: AppEntry): boolean {
  if (entry.kind === 'event') return isUndoBoundaryEvent(entry.event)
  // Transactions: boundary if any event is a boundary
  return entry.events.some(isUndoBoundaryEvent)
}

function isUndoBoundaryEvent(event: EventTuple): boolean {
  const type = event[0]
  if (typeof type !== 'string') return false
  if (type.startsWith('paint.')) return true
  // Palette edits (colour, material, density, items) are steps of their own (owner, 2026-10-15:
  // "undo - fix"); a gesture's live edits are folded into one entry (atoms.ts, `coalescePaletteEdit`).
  if (type.startsWith('palette.')) return true
  // scribble.apply: durable scribble stroke (undo boundary)
  // scribble.append: incremental draft stroke (NOT a boundary; coalesced to apply on commit)
  if (type === 'scribble.apply') return true
  if (type === 'scribble.append') return false
  // brush.stroke: durable swept stroke (undo boundary); brush.stroke.append: draft increment
  if (type === 'brush.stroke') return true
  if (type === 'brush.stroke.append') return false
  if (type.startsWith('voxel.')) return true
  // experience.<world>.<verb>: durable world events (src/experiences) are undo boundaries
  if (type.startsWith('experience.')) return true
  // Committing a selection applies changes to the document; treat as boundary
  if (type === 'doc.selection.commit') return true
  // selection.* mostly affects UI only; keep for replay but not a boundary
  if (type === 'selection.change.clear' || type === 'doc.selection.delete') return true
  if (type.startsWith('selection.')) return false
  // tool/ui/overlay/geom/layer.* (other) are not boundaries
  return false
}
